import { Accordion } from '@zillow/constellation-10';

import { type ExpandedGroups, useExpandedGroups } from '../../hooks/useExpandedGroups';

import { accordionItemStyles, accordionRootStyles } from './styles';

import type { ReactNode } from 'react';

// XOR so a controlled list and a storage key can't disagree about which ids are open.
type ExpansionProps = { storageKey: string; expansion?: never } | { expansion: ExpandedGroups; storageKey?: never };

type Props<T> = {
  items: Array<T>;
  getItemId: (item: T) => string;
  renderCard: (item: T, isExpanded: boolean) => ReactNode;
  ariaLabel: string;
  // Receives each card's element, e.g. to scroll one into view.
  registerCard?: (id: string, node: HTMLLIElement | null) => void;
  'data-testid'?: string;
} & ExpansionProps;

/**
 * A vertical list of independently expandable cards. Owns the Accordion wiring and the
 * session-persisted expanded state; callers supply the card itself.
 */
export const AccordionCardList = <T,>({
  items,
  getItemId,
  renderCard,
  storageKey,
  ariaLabel,
  expansion,
  registerCard,
  'data-testid': dataTestId,
}: Props<T>) => {
  // Hooks can't be conditional; the fallback key is inert because a controlled list never reads it.
  const ownExpansion = useExpandedGroups(storageKey ?? '');
  const { expandedIds, isExpanded, onExpandedChange } = expansion ?? ownExpansion;

  return (
    <Accordion.Root
      data-testid={dataTestId}
      aria-label={ariaLabel}
      expanded={expandedIds}
      onExpandedChange={onExpandedChange}
      css={accordionRootStyles}
    >
      {items.map((item) => {
        const itemId = getItemId(item);

        return (
          <Accordion.Item
            key={itemId}
            value={itemId}
            ref={registerCard ? (node) => registerCard(itemId, node) : undefined}
            css={accordionItemStyles}
          >
            {renderCard(item, isExpanded(itemId))}
          </Accordion.Item>
        );
      })}
    </Accordion.Root>
  );
};

export default AccordionCardList;
